/**
 * LLM Model Editor Component
 * Comprehensive editor for LLM Model entities with tabs for organized fields
 * Tabs: Basic | Model Info | Capabilities | Costs | API | Display
 */

import { LockIcon, BugIcon, LightbulbIcon, ClipboardIcon, ZapIcon } from '@bizfirst/common-react';
import React, { useState, useEffect } from 'react';
import { ModalPortal } from '../../Modals/ModalPortal';
import LookupSelect from '../../Modals/LookupSelect';
import { llmModelApiClient } from '@flow-studio/api';
import { lookupConfigs } from '../../../lookupConfigs';
import { lookupEventBus } from 'flow-studio-core';
import { useUIStore } from '@flow-studio/store';
import { CostGrid } from '../../Shared/CostGrid';
import { Loader } from '../../Shared/Loader';
import '../Agent/AgentEditor.css'; // Reuse agent editor styles
import './LLMModelEditor.css';

export interface LLMModelFormData {
  LlmModelID?: number;
  ResID?: string;
  ModelName: string;
  Description: string;
  ShortDescription: string;
  LongDescription: string;
  ModelCode: string;
  Version: string;
  LlmProviderID: number | null;
  ModelCategoryID: number | null;
  ModelTypeID: number | null;
  IsMultiModel: boolean;
  IsRealTime: boolean;
  Dimension: number | null;
  TextInputCost: number | null;
  CachedTextInputCost: number | null;
  AudioInputCost: number | null;
  CachedAudioInputCost: number | null;
  TextOutputCost: number | null;
  AudioOutputCost: number | null;
  ApiEndpoint: string;
  ApiKey: string;
  IconUrl: string;
  ImageUrl: string;
  HelpDocumentation: string;
  DisplayOrder: number;
  Enabled: boolean;
  IsActive?: boolean;
  CreatedOn?: string;
  CreatedBy?: string;
  LastModifiedOn?: string;
  LastModifiedBy?: string;
}

interface LLMModelEditorProps {
  llmModelId?: number;
  isOpen: boolean;
  onClose: () => void;
  onLLMModelSaved?: (model: LLMModelFormData) => void;
}

type ModelTab = 'basic' | 'model-info' | 'capabilities' | 'costs' | 'api' | 'display' | 'security' | 'debugger';

const defaultFormData: LLMModelFormData = {
  ModelName: '',
  Description: '',
  ShortDescription: '',
  LongDescription: '',
  ModelCode: '',
  Version: '',
  LlmProviderID: null,
  ModelCategoryID: null,
  ModelTypeID: null,
  IsMultiModel: false,
  IsRealTime: false,
  Dimension: null,
  TextInputCost: null,
  CachedTextInputCost: null,
  AudioInputCost: null,
  CachedAudioInputCost: null,
  TextOutputCost: null,
  AudioOutputCost: null,
  ApiEndpoint: '',
  ApiKey: '',
  IconUrl: '',
  ImageUrl: '',
  HelpDocumentation: '',
  DisplayOrder: 0,
  Enabled: true,
  IsActive: true,
};

export const LLMModelEditor: React.FC<LLMModelEditorProps> = ({
  llmModelId,
  isOpen,
  onClose,
  onLLMModelSaved,
}) => {
  const { showToast } = useUIStore();
  const [activeTab, setActiveTab] = useState<ModelTab>('basic');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [formData, setFormData] = useState<LLMModelFormData>(defaultFormData);

  // Load LLM Model on open
  useEffect(() => {
    if (isOpen) {
      if (llmModelId) {
        loadLLMModelData();
        setIsEditing(false);
      } else {
        resetForm();
        setIsEditing(true);
      }
    }
  }, [isOpen, llmModelId]);

  const loadLLMModelData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await llmModelApiClient.getById({ id: llmModelId! });
      if (response.success && response.data) {
        setFormData(response.data as unknown as LLMModelFormData);
      } else {
        setError(response.error?.message || 'Failed to load LLM Model');
      }
    } catch (err: any) {
      setError(err.message || 'Error loading LLM Model');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(defaultFormData);
    setError(null);
    setIsDirty(false);
  };

  const handleRefresh = async () => {
    if (llmModelId) {
      await loadLLMModelData();
    }
  };

  const handleChange = (field: keyof LLMModelFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsDirty(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.ModelName.trim()) {
      setError('Model name is required');
      return;
    }

    setLoading(true);
    try {
      const response = llmModelId
        ? await llmModelApiClient.update(llmModelId, formData)
        : await llmModelApiClient.create(formData);

      if (response.success && response.data) {
        //  Auto-refresh related lookups
        if (!llmModelId) {
          lookupEventBus.emitMultiple([
            'lookup_llmProvider',
            'lookup_modelCategory',
            'lookup_modelType',
          ]);
        }

        onLLMModelSaved?.(response.data as unknown as LLMModelFormData);
        onClose();
      } else {
        setError(response.error?.message || 'Failed to save LLM Model');
      }
    } catch (err: any) {
      setError(err.message || 'Error saving LLM Model');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      showToast('info', 'Changes discarded');
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalPortal>
      <div className="agent-editor-overlay" onClick={handleCancel}>
        <div
          className="agent-editor-container"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="agent-editor-header">
            <h1 className="editor-title">
              {llmModelId ? 'Edit LLM Model' : 'Create LLM Model'}
            </h1>
            <div className="editor-header-actions">
              <button className="close-btn" onClick={handleCancel}>
                ✕
              </button>
            </div>
          </div>

          {/* Error Banner */}
          {error && (
            <div className="editor-error-banner">
              <span>{error}</span>
            </div>
          )}

          {/* Loading State */}
          {loading && !llmModelId && (
            <div className="editor-loading">
              <Loader label="Loading..." />
            </div>
          )}

          {/* Content */}
          {!loading && (
            <form onSubmit={handleSave} className="editor-form">
              {/* Tabs */}
              <div className="top-tabs">
                {(['basic', 'model-info', 'capabilities', 'costs', 'api', 'display', 'security', 'debugger'] as ModelTab[]).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    className={`tab ${activeTab === tab ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab === 'basic' && 'Basic'}
                    {tab === 'model-info' && 'Model Info'}
                    {tab === 'capabilities' && 'Capabilities'}
                    {tab === 'costs' && 'Costs'}
                    {tab === 'api' && 'API'}
                    {tab === 'display' && 'Display'}
                    {tab === 'security' && <><LockIcon size={12} /> Security</>}
                    {tab === 'debugger' && <><BugIcon size={12} /> Debugger</>}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="tab-content">
                {/* Basic Tab */}
                {activeTab === 'basic' && (
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Model Name *</label>
                      <input
                        type="text"
                        value={formData.ModelName}
                        onChange={(e) => handleChange('ModelName', e.target.value)}
                        disabled={!isEditing}
                        placeholder="e.g., GPT-4 Turbo"
                      />
                    </div>

                    <div className="form-group">
                      <label>Model Code</label>
                      <input
                        type="text"
                        value={formData.ModelCode}
                        onChange={(e) => handleChange('ModelCode', e.target.value)}
                        disabled={!isEditing}
                        placeholder="e.g., gpt-4-turbo"
                      />
                    </div>

                    <div className="form-group full">
                      <label>Description</label>
                      <textarea
                        value={formData.Description}
                        onChange={(e) => handleChange('Description', e.target.value)}
                        disabled={!isEditing}
                        placeholder="Brief description of the model"
                        rows={3}
                      />
                    </div>

                    <div className="form-group full">
                      <label>Short Description</label>
                      <input
                        type="text"
                        value={formData.ShortDescription}
                        onChange={(e) => handleChange('ShortDescription', e.target.value)}
                        disabled={!isEditing}
                        placeholder="One-line summary"
                        maxLength={500}
                      />
                    </div>

                    <div className="form-group full">
                      <label>Long Description</label>
                      <textarea
                        value={formData.LongDescription}
                        onChange={(e) => handleChange('LongDescription', e.target.value)}
                        disabled={!isEditing}
                        placeholder="Detailed description"
                        rows={4}
                      />
                    </div>
                  </div>
                )}

                {/* Model Info Tab */}
                {activeTab === 'model-info' && (
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Version</label>
                      <input
                        type="text"
                        value={formData.Version}
                        onChange={(e) => handleChange('Version', e.target.value)}
                        disabled={!isEditing}
                        placeholder="e.g., 1.0.0"
                      />
                    </div>

                    <div className="form-group">
                      <LookupSelect
                        fetchConfig={lookupConfigs.llmProvider}
                        value={formData.LlmProviderID}
                        onChange={(value) => handleChange('LlmProviderID', value)}
                        label="LLM Provider"
                        required={false}
                        disabled={!isEditing}
                        placeholder="Select LLM provider (optional)..."
                        searchable={true}
                        maxItemsInDropdown={50}
                      />
                    </div>

                    <div className="form-group">
                      <LookupSelect
                        fetchConfig={lookupConfigs.modelCategory}
                        value={formData.ModelCategoryID}
                        onChange={(value) => handleChange('ModelCategoryID', value)}
                        label="Model Category"
                        required={false}
                        disabled={!isEditing}
                        placeholder="Select category (optional)..."
                        searchable={true}
                        maxItemsInDropdown={50}
                      />
                    </div>

                    <div className="form-group">
                      <LookupSelect
                        fetchConfig={lookupConfigs.modelType}
                        value={formData.ModelTypeID}
                        onChange={(value) => handleChange('ModelTypeID', value)}
                        label="Model Type"
                        required={false}
                        disabled={!isEditing}
                        placeholder="Select type (optional)..."
                        searchable={true}
                        maxItemsInDropdown={50}
                      />
                    </div>
                  </div>
                )}

                {/* Capabilities Tab */}
                {activeTab === 'capabilities' && (
                  <div className="form-grid">
                    <div className="checkbox-grid">
                      <label className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={formData.IsMultiModel}
                          onChange={(e) =>
                            handleChange('IsMultiModel', e.target.checked)
                          }
                          disabled={!isEditing}
                        />
                        <span>Supports Multiple Modalities</span>
                      </label>

                      <label className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={formData.IsRealTime}
                          onChange={(e) => handleChange('IsRealTime', e.target.checked)}
                          disabled={!isEditing}
                        />
                        <span>Supports Real-Time Streaming</span>
                      </label>
                    </div>

                    <div className="form-group">
                      <label>Embedding Dimension</label>
                      <input
                        type="number"
                        value={formData.Dimension ?? ''}
                        onChange={(e) =>
                          handleChange(
                            'Dimension',
                            e.target.value ? parseInt(e.target.value) : null
                          )
                        }
                        disabled={!isEditing}
                        placeholder="e.g., 1536"
                      />
                    </div>
                  </div>
                )}

                {/* Costs Tab */}
                {activeTab === 'costs' && (
                  <CostGrid
                    costs={{
                      textInputCost: formData.TextInputCost ?? undefined,
                      cachedTextInputCost: formData.CachedTextInputCost ?? undefined,
                      audioInputCost: formData.AudioInputCost ?? undefined,
                      cachedAudioInputCost: formData.CachedAudioInputCost ?? undefined,
                      textOutputCost: formData.TextOutputCost ?? undefined,
                      audioOutputCost: formData.AudioOutputCost ?? undefined,
                    }}
                    onChange={(field, value) => {
                      const fieldMap: { [key: string]: keyof LLMModelFormData } = {
                        textInputCost: 'TextInputCost',
                        cachedTextInputCost: 'CachedTextInputCost',
                        audioInputCost: 'AudioInputCost',
                        cachedAudioInputCost: 'CachedAudioInputCost',
                        textOutputCost: 'TextOutputCost',
                        audioOutputCost: 'AudioOutputCost',
                      };
                      handleChange(fieldMap[field], value);
                    }}
                    disabled={!isEditing}
                  />
                )}

                {/* API Tab */}
                {activeTab === 'api' && (
                  <div className="form-grid">
                    <div className="form-group full">
                      <label>API Endpoint</label>
                      <input
                        type="text"
                        value={formData.ApiEndpoint}
                        onChange={(e) => handleChange('ApiEndpoint', e.target.value)}
                        disabled={!isEditing}
                        placeholder="https://api.example.com/v1"
                      />
                    </div>

                    <div className="form-group full">
                      <label>API Key</label>
                      <input
                        type="password"
                        value={formData.ApiKey}
                        onChange={(e) => handleChange('ApiKey', e.target.value)}
                        disabled={!isEditing}
                        placeholder="Your API key (encrypted)"
                      />
                    </div>
                  </div>
                )}

                {/* Display Tab */}
                {activeTab === 'display' && (
                  <div className="form-grid">
                    <div className="form-group full">
                      <label>Icon URL</label>
                      <input
                        type="text"
                        value={formData.IconUrl}
                        onChange={(e) => handleChange('IconUrl', e.target.value)}
                        disabled={!isEditing}
                        placeholder="https://example.com/icon.png"
                      />
                    </div>

                    <div className="form-group full">
                      <label>Image URL</label>
                      <input
                        type="text"
                        value={formData.ImageUrl}
                        onChange={(e) => handleChange('ImageUrl', e.target.value)}
                        disabled={!isEditing}
                        placeholder="https://example.com/image.png"
                      />
                    </div>

                    <div className="form-group full">
                      <label>Help Documentation</label>
                      <textarea
                        value={formData.HelpDocumentation}
                        onChange={(e) =>
                          handleChange('HelpDocumentation', e.target.value)
                        }
                        disabled={!isEditing}
                        placeholder="Markdown documentation"
                        rows={4}
                      />
                    </div>

                    <div className="form-group">
                      <label>Display Order</label>
                      <input
                        type="number"
                        value={formData.DisplayOrder}
                        onChange={(e) =>
                          handleChange('DisplayOrder', parseInt(e.target.value) || 0)
                        }
                        disabled={!isEditing}
                      />
                    </div>

                    <div className="checkbox-grid">
                      <label className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={formData.Enabled}
                          onChange={(e) => handleChange('Enabled', e.target.checked)}
                          disabled={!isEditing}
                        />
                        <span>Enabled</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Security Tab */}
                {activeTab === 'security' && (
                  <div className="form-grid read-only">
                    <div className="form-group">
                      <label>Primary ID</label>
                      <div className="display-value">{formData.LlmModelID || 'N/A'}</div>
                    </div>

                    <div className="form-group">
                      <label>Resource ID (ResID)</label>
                      <div className="display-value">{formData.ResID || 'N/A'}</div>
                    </div>

                    <div className="form-group">
                      <label>Created On</label>
                      <div className="display-value">
                        {formData.CreatedOn ? new Date(formData.CreatedOn).toLocaleString() : 'N/A'}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Created By</label>
                      <div className="display-value">{formData.CreatedBy || 'N/A'}</div>
                    </div>

                    <div className="form-group">
                      <label>Last Modified On</label>
                      <div className="display-value">
                        {formData.LastModifiedOn ? new Date(formData.LastModifiedOn).toLocaleString() : 'N/A'}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Last Modified By</label>
                      <div className="display-value">{formData.LastModifiedBy || 'N/A'}</div>
                    </div>
                  </div>
                )}

                {/* Debugger Tab */}
                {activeTab === 'debugger' && (
                  <div style={{ padding: '20px' }}>
                    <div style={{ marginBottom: '12px' }}>
                      <h4 style={{ margin: '0 0 12px 0', color: '#f8fafc', fontSize: '14px' }}><ClipboardIcon size={12} /> Entity Data (JSON)</h4>
                      <textarea
                        value={JSON.stringify(formData, null, 2)}
                        readOnly
                        style={{
                          width: '100%',
                          height: '400px',
                          padding: '12px',
                          background: 'rgba(0,0,0,0.4)',
                          border: '1px solid rgba(58, 140, 69, 0.3)',
                          borderRadius: '6px',
                          color: '#3A8C45',
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          resize: 'vertical',
                          cursor: 'default',
                        }}
                      />
                    </div>
                    <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                      <LightbulbIcon size={12} /> Tip: Copy this JSON data for debugging or API requests
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="editor-footer">
                {isEditing ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setIsEditing(false);
                        resetForm();
                        if (llmModelId) loadLLMModelData();
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                  </>
                ) : (
                  <>
                    {llmModelId && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleRefresh}
                        disabled={loading}
                        title="Refresh data from server"
                      >
                        <ZapIcon size={14} /> Refresh
                      </button>
                    )}
                    {llmModelId && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setIsEditing(true)}
                        title="Enable edit mode"
                      >
                        Edit
                      </button>
                    )}
                  </>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};

export default LLMModelEditor;
